import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../store/projectStore';
import { useAuthStore } from '../store/authStore';
import { Archive, ArchiveRestore, Calendar, User, DollarSign, AlertCircle, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const statusColors = {
  A_PREPARER: 'bg-gray-100 text-gray-800',
  EN_COURS: 'bg-blue-100 text-blue-800',
  VALIDE: 'bg-green-100 text-green-800',
  DEBLOCAGE_FONDS: 'bg-purple-100 text-purple-800',
  TERMINE: 'bg-emerald-100 text-emerald-800',
  REFUSE: 'bg-red-100 text-red-800',
};

const statusLabels = {
  A_PREPARER: 'À préparer',
  EN_COURS: 'En cours',
  VALIDE: 'Validé',
  DEBLOCAGE_FONDS: 'Déblocage fonds',
  TERMINE: 'Terminé',
  REFUSE: 'Refusé',
};

export default function ArchivedProjectsPage() {
  const navigate = useNavigate();
  const { archivedProjects, isLoading, fetchArchivedProjects, unarchiveProject } = useProjectStore();
  const { user } = useAuthStore();
  const [unarchivingId, setUnarchivingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!['ACCOMPAGNATEUR', 'COORDINATEUR', 'ADMIN'].includes(user.profile_type || '')) {
      navigate('/dashboard');
      return;
    }
    fetchArchivedProjects();
  }, [fetchArchivedProjects, user, navigate]);

  const handleUnarchive = async (id: string) => {
    if (!confirm('Voulez-vous vraiment désarchiver ce projet ?')) return;

    setUnarchivingId(id);
    try {
      await unarchiveProject(id);
    } catch (error) {
      console.error('Error unarchiving project:', error);
      alert('Erreur lors du désarchivage du projet');
    } finally {
      setUnarchivingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>

          <div className="flex items-center gap-3 mb-2">
            <Archive className="w-8 h-8 text-gray-600" />
            <h1 className="text-3xl font-bold text-gray-900">Projets Archivés</h1>
          </div>
          <p className="text-gray-600">
            Consultez et gérez les projets archivés. Seuls les accompagnateurs et coordinateurs peuvent accéder à cette section.
          </p>
        </div>

        {archivedProjects.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun projet archivé</h3>
            <p className="text-gray-600">
              Les projets archivés apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {archivedProjects.map((project) => (
              <div
                key={project.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{project.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[project.status as keyof typeof statusColors]}`}>
                        {statusLabels[project.status as keyof typeof statusLabels]}
                      </span>
                    </div>
                    <p className="text-gray-600 line-clamp-2">{project.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="w-4 h-4" />
                    <span>
                      <span className="font-medium">Porteur:</span> {project.owner?.full_name || 'Non assigné'}
                    </span>
                  </div>

                  {project.assigned_advisor && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="w-4 h-4" />
                      <span>
                        <span className="font-medium">Accompagnateur:</span> {project.assigned_advisor.full_name}
                      </span>
                    </div>
                  )}

                  {project.funding_amount && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      <span>
                        <span className="font-medium">Financement:</span>{' '}
                        {new Intl.NumberFormat('fr-FR', {
                          style: 'currency',
                          currency: 'EUR',
                        }).format(project.funding_amount)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Archivé le{' '}
                        {project.archived_at
                          ? format(new Date(project.archived_at), 'dd MMM yyyy', { locale: fr })
                          : 'Date inconnue'}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleUnarchive(project.id)}
                    disabled={unarchivingId === project.id}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArchiveRestore className="w-4 h-4" />
                    {unarchivingId === project.id ? 'Désarchivage...' : 'Désarchiver'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Information</p>
              <p>
                Les projets archivés ne sont plus visibles dans la liste principale des projets.
                Vous pouvez les désarchiver à tout moment pour les rendre à nouveau accessibles.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
